import { useEffect, useRef, useState } from 'react'

import { useIsModalOpen } from '@/hooks/useModalOpen'
import { registerBrowserSessionCleanup } from '@/lib/auth/browser-session-boundary'
import { getWorkerBearerToken } from '@/lib/auth/client'

type NTChat = ((command: string, ...args: unknown[]) => void) & { q?: unknown[][]; l?: 1 }

declare global {
  interface Window {
    NTChat?: NTChat
  }
}

export const SUPPORT_CHAT_ORIGINS = {
  production: 'https://support-api.nonresident.tax',
  staging: 'https://support-api-staging.nonresident.tax',
  // portless serves apps/support at this name under `bun run dev`.
  local: 'https://support.nonresidenttax.localhost',
} as const

/**
 * The support Worker that serves chat for this host; null where native chat does not run.
 * A `*.localhost` page is served by the portless proxy, which serves the support Worker on
 * the same port (443, or 1355 when it runs without root).
 */
export function supportChatOrigin(hostname: string, port = ''): string | null {
  if (hostname === 'app.nonresident.tax') return SUPPORT_CHAT_ORIGINS.production
  if (hostname === 'staging.nonresident.tax') return SUPPORT_CHAT_ORIGINS.staging
  if (hostname.endsWith('.localhost') && /^\d+$/.test(port)) return `${SUPPORT_CHAT_ORIGINS.local}:${port}`
  if (hostname === 'localhost' || hostname.endsWith('.localhost')) return SUPPORT_CHAT_ORIGINS.local
  return null
}

const LOADER_ID = 'ntchat-loader'

/** Where the chat links land: `?chat=open` (via `/sign-in?next=` from the support welcome) or `#ntchat`. */
function linkOpensChat(): boolean {
  try {
    return new URLSearchParams(window.location.search).get('chat') === 'open' || window.location.hash === '#ntchat'
  } catch {
    return false
  }
}

/** `window.NTChat`, as the queueing stub until the loader replaces it. */
function ntChat(): NTChat {
  if (!window.NTChat) {
    const stub: NTChat = (...args: unknown[]) => {
      ;(stub.q = stub.q ?? []).push(args)
    }
    window.NTChat = stub
  }
  return window.NTChat
}

/** Loads the loader once per page; a script that cannot load leaves no tag behind. */
function loadOnce(origin: string): void {
  if (document.getElementById(LOADER_ID)) return
  const script = document.createElement('script')
  script.id = LOADER_ID
  script.src = `${origin}/chat/v1/loader.js`
  script.async = true
  script.onerror = () => script.remove()
  document.body.appendChild(script)
}

/**
 * The native live chat bubble (apps/support widget) for signed-in customers. The host passes
 * the Better Auth JWT to the chat iframe only through the loader's origin-checked `getToken`
 * bridge. Where chat does not run (this host not in the support Worker's `CHAT_HOSTS`, or
 * chat switched off) the loader reports `unavailable` and the page has no chat bubble.
 */
export function SupportChat({ userId }: { userId?: string | null }) {
  const [origin] = useState(() =>
    typeof window === 'undefined' ? null : supportChatOrigin(window.location.hostname, window.location.port),
  )
  const isModalOpen = useIsModalOpen()
  const signedIn = Boolean(userId)
  const openedFromLink = useRef(false)

  useEffect(() => {
    if (!origin) return
    loadOnce(origin)
    ntChat()('show')
    // The loader is page-global: leaving the layout hides the bubble, signing out resets it.
    return () => window.NTChat?.('hide')
  }, [origin])

  useEffect(() => {
    if (!origin || !signedIn) return
    ntChat()('identify', { getToken: () => getWorkerBearerToken() })
    // A visitor following "Continue in your account" lands signed in with the chat open (once).
    if (!openedFromLink.current && linkOpensChat()) {
      openedFromLink.current = true
      ntChat()('open')
    }
  }, [origin, signedIn, userId])

  useEffect(() => {
    if (origin) ntChat()(isModalOpen ? 'hide' : 'show')
  }, [origin, isModalOpen])

  useEffect(() => registerBrowserSessionCleanup(() => window.NTChat?.('reset')), [])

  return null
}
